import { UsageIcon, BatteryIcon } from '../components/common/Icons';

/**
 * Formats a duration string in "HH:MM:SS" format into a more readable format.
 * @param {string} durationStr - A string in the format "HH:MM:SS".
 * @returns {string} The formatted duration (e.g., "2 hr 30 min", "36 min", etc.).
 */
function formatDuration(durationStr) {
  if (!durationStr) return '';

  const [hoursStr, minutesStr] = durationStr.split(':');
  const hours = parseInt(hoursStr, 10) || 0;
  const minutes = parseInt(minutesStr, 10) || 0;

  // If there are no hours, display only minutes
  if (hours === 0) {
    return `${minutes} minutes`;
  }
  // If there are hours but less than 24, display "X hr Y min"
  if (hours < 24) {
    return `${hours} hr ${minutes} min`;
  }
  // If it exceeds 24 hours, also display days
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `${days} day${days > 1 ? 's' : ''} ${remainingHours} hr ${minutes} min`;
}

const hasValidBatteryData = (data, hasError) => {
  if (hasError) return false;
  if (!Array.isArray(data?.results)) return false;

  const percentage = data.results[0]?.percentage;
  return percentage != null;
};

// Define the desired order of cards on mobile (vertical) view
export const mobileOrder = ['usage', 'water', 'location', 'battery'];
// Define the desired order of cards on desktop (horizontal) view
export const desktopOrder = ['usage', 'location', 'water', 'battery'];

/**
 * Generates an array of configuration objects for rendering multiple CardV2 components.
 * This function transforms raw usage, location and water change data, along with error flags, into
 * formatted card configurations. Each configuration object includes the icon, title, URL,
 * main metric, and additional details. The location card's details are rendered using the
 * CardLocationDetails component, which formats the raw location data internally.
 *
 * @param {Object} params - Object containing the raw data and error flags.
 * @param {Object} params.usageData - Raw usage data containing the total usage time for the day.
 * @param {Object} params.locationData - Raw location data.
 * @param {Object} params.waterChangeData - Water change duration data.
 * @param {Object} params.errors - Error flags (usage, location, waterChange).
 * @param {String} params.requestTimestamp - Timestamp that indicates when the request was done the last time
 * @returns {Array} Array of configuration objects for CardV2 components.
 */
const getCardConfigs = ({ usageData, batteryLifeData, errors, requestTimestamp, cardErrors }) => {
  const { usage: hasUsageError, batteryLife: hasBatteryLifeError } = errors;

  const { usage: hasUsageCardError, batteryLife: hasBatteryLifeCardError } = cardErrors;

  const getConditionalString = (hasError, defaultString) => (hasError ? '' : defaultString);

  // USAGE CARD
  const usageCopy = getConditionalString(hasUsageError, `Total usage ${usageData?.date}`);
  const usageMetric = getConditionalString(hasUsageError, formatDuration(usageData?.total_usage));
  const usageCardError = hasUsageCardError || !usageData?.date || !usageData?.total_usage;

  // LOCATION CARD
  // Temporarily commented out the Location Card.
  // Pending final decision on whether it should be permanently removed from the dashboard.
  /*const locationCopy = getConditionalString(
    hasLocationError,
    `Locations visited on ${locationData?.date}`,
  );
  const locationMetric =
    !hasLocationError && locationData
      ? locationData?.total_cleaned_locations?.toString() || ''
      : '';
  const locationCardError = hasLocationCardError || !locationData?.total_cleaned_locations;
  */

  // WATER CHANGE CARD
  /*
  const waterChangeCopy =
    !hasWaterChangeError && waterChangeData?.date ? `Time spent on ${waterChangeData.date}` : '';
  const waterChangeMetric =
    !hasWaterChangeError && waterChangeData?.unit_day_total_duration
      ? formatDuration(waterChangeData.unit_day_total_duration)
      : '';
*/

  const batteryMainMetric = hasValidBatteryData(batteryLifeData, hasBatteryLifeError)
    ? batteryLifeData.results[0].percentage
    : null;

  return [
    {
      id: 'usage',
      icon: UsageIcon,
      title: 'Usage history',
      copy: usageCopy,
      url: '/usage',
      mainMetric: usageMetric,
      details: null,
      cardError: usageCardError,
    },
    /*
    {
      id: 'location',
      icon: LocationIcon,
      title: 'Location history',
      copy: locationCopy,
      url: '/location',
      mainMetric: locationMetric,
      details: null,
      cardError: locationCardError,
    },
    {
      id: 'water',
      icon: WaterIcon,
      title: 'Water change',
      copy: waterChangeCopy,
      url: '/waterChanges',
      mainMetric: waterChangeMetric,
      details: null,
      cardError: hasWaterChangeCardError,
    },*/
    {
      id: 'battery',
      icon: BatteryIcon,
      title: 'Battery Life',
      copy: '',
      url: '/battery',
      mainMetric: batteryMainMetric,
      details: null,
      requestTimestamp,
      cardError: hasBatteryLifeCardError,
    },
  ];
};

export default getCardConfigs;
